import { CDN_URL } from '../../js/config.js';
import { storageManager } from '../storage/index.js';
import { storage } from '../../js/utils/storage.js';

export class BackgroundService {
    constructor() {
        this.CDN_BASE_URL = `${CDN_URL}/data/backgrounds_images/`;
        this.subscribers = [];

        this.timeCodeMap = { day: '1', sunset: '2', night: '3' };
        this.timeSlotMap = { '1': 'day', '2': 'sunset', '3': 'night' };
        this.numericTimeMap = {
            '0.1': 'day', '1': 'day',
            '0.2': 'sunset', '2': 'sunset',
            '0.3': 'night', '3': 'night'
        };

        this._cache = null;
        this._cacheTime = 0;
        this._cacheTTL = 500;
        this._inflight = null;
        this._cacheGeneration = 0;
    }

    async _determineTimeOfDay(config) {
        let timeOfDay = 'night';
        try {
            if (typeof window !== 'undefined' && window.timeProgressionService) {
                const currentTime = window.timeProgressionService.getCurrentTime();
                timeOfDay = this.hourToTimeOfDay(currentTime);
                console.log('BackgroundService: Got time from TimeProgressionService:', currentTime, '->', timeOfDay);
            } else if (config.timeOfDay) {
                // this handles manual time changes
                timeOfDay = config.timeOfDay;
                console.log('BackgroundService: Got time from config.timeOfDay:', timeOfDay);
            } else {
                // Fallback to TimeDefinitionService for consistent time handling
                try {
                    const { getTimeDefinitionService } = await import('./TimeDefinitionService.js');
                    const timeService = getTimeDefinitionService();
                    const currentHour = new Date().getHours();
                    timeOfDay = timeService.hourToTimeOfDay(currentHour);
                    console.log('BackgroundService: Got time from TimeDefinitionService using current hour:', currentHour, '->', timeOfDay);
                } catch (timeServiceError) {
                    console.warn('BackgroundService: Could not load TimeDefinitionService, using default:', timeServiceError);
                    timeOfDay = 'night';
                }
            }
        } catch (error) {
            console.warn('BackgroundService: Error determining time of day:', error);
            timeOfDay = config.timeOfDay || 'night';
        }

        return this.numericTimeMap[timeOfDay] || timeOfDay;
    }

    async getCurrentBackground() {
        const now = Date.now();
        if (this._cache && (now - this._cacheTime) < this._cacheTTL) {
            return this._cache;
        }
        if (this._inflight) return this._inflight;

        const generation = this._cacheGeneration;
        const inflight = (async () => {
            try {
                const config = await storageManager.getItem('currentHangoutConfig') || {};

                let loc_id = 1;
                console.log('BackgroundService: getCurrentBackground() starting with config:', config);

                try {
                    // Priority order for loc_id:
                    // 1. User-set location in config (from settings modal) - highest priority
                    // 2. Scenario location (for new conversations without user override)
                    // 3. State reconstruction from messages (for resumed conversations)

                    // Check if config already has a valid, user-set loc_id
                    // Valid means: not placeholder values like '0', '1', 'ERROR_NO_LOCATION', etc.
                    const isValidUserSetLocation = config.loc_id &&
                        config.loc_id !== '0' &&
                        config.loc_id !== '1' &&
                        !String(config.loc_id).startsWith('ERROR');

                    if (isValidUserSetLocation) {
                        // User has set a location via settings - use it regardless of isNewConversation
                        loc_id = config.loc_id;
                        console.log('BackgroundService: Using user-set loc_id from config:', loc_id);
                    } else if (config.isNewConversation && config.scenarioId) {
                        // New conversation without user override - get from scenario
                        try {
                            const { HangoutState } = await import('../../js/hangout/state/HangoutState.js');
                            const scenario = HangoutState.getInstance().getSelectedScenario();
                            if (scenario?.loc) {
                                loc_id = scenario.loc;
                                config.loc_id = loc_id;
                                await storageManager.setItem('currentHangoutConfig', config);
                                console.log('BackgroundService: New conversation - Using scenario loc:', loc_id);
                            }
                        } catch (scenarioError) {
                            console.warn('BackgroundService: Error getting scenario for new conversation:', scenarioError);
                        }
                    } else if (!config.isNewConversation) {
                        try {
                            const { HangoutState } = await import('../../js/hangout/state/HangoutState.js');
                            const hangoutState = HangoutState.getInstance();

                            const messages = hangoutState.getMessages() || [];
                            let latestLocId = null;

                            for (let i = messages.length - 1; i >= 0; i--) {
                                const msg = messages[i];
                                if (msg.state_diff && (msg.state_diff.loc_id || msg.state_diff.loc)) {
                                    latestLocId = msg.state_diff.loc_id || msg.state_diff.loc;
                                    break;
                                }
                            }

                            if (latestLocId) {
                                loc_id = latestLocId;
                                console.log('BackgroundService: Resuming conversation - Got loc_id from message state_diff:', loc_id);
                            } else if (config.loc_id) {
                                // Fallback to config if no reconstructed state
                                loc_id = config.loc_id;
                                console.log('BackgroundService: Resuming conversation - Got loc_id from config (fallback):', loc_id);
                            }
                        } catch (stateError) {
                            console.warn('BackgroundService: Error getting reconstructed state:', stateError);
                            // Fallback to config
                            if (config.loc_id) {
                                loc_id = config.loc_id;
                                console.log('BackgroundService: Resuming conversation - Got loc_id from config (error fallback):', loc_id);
                            }
                        }
                    }

                    if (loc_id == 1 && config.backgroundUrl && !config.loc_id) {
                        const match = config.backgroundUrl.match(/(\d+)\.\d+\.webp/);
                        if (match) {
                            loc_id = match[1];
                            console.log('BackgroundService: Got loc_id from backgroundUrl:', loc_id);
                        }
                    }

                } catch (locationError) {
                    console.warn('BackgroundService: Error during location detection, using fallback:', locationError);
                    loc_id = (config.loc_id && config.loc_id !== '0') ? config.loc_id : '1';
                    console.log('BackgroundService: Fallback loc_id selected:', loc_id);
                }

                console.log('BackgroundService: Final loc_id determined:', loc_id);

                // Skip background fetching for placeholder locations
                if (loc_id === 0 || loc_id === 1) {
                    const placeholder = {
                        locationId: String(loc_id),
                        timeOfDay: 'night',
                        backgroundUrl: null,
                        isPlaceholder: true
                    };
                    console.log('BackgroundService: Skipping background fetch for placeholder location:', loc_id);
                    if (generation === this._cacheGeneration) {
                        this._cache = placeholder;
                        this._cacheTime = Date.now();
                    }
                    return placeholder;
                }

                const timeOfDay = await this._determineTimeOfDay(config);
                const backgroundUrl = this.constructBackgroundUrl(loc_id, timeOfDay);

                const result = { locationId: String(loc_id), timeOfDay, backgroundUrl };
                if (generation === this._cacheGeneration) {
                    this._cache = result;
                    this._cacheTime = Date.now();
                }

                console.log('BackgroundService: getCurrentBackground() returning:', result);

                return result;
            } catch {
                console.log('BackgroundService: Error in getCurrentBackground, returning placeholder');
                return {
                    locationId: '1',
                    timeOfDay: 'night',
                    backgroundUrl: null,
                    isPlaceholder: true
                };
            } finally {
                if (this._inflight === inflight) this._inflight = null;
            }
        })();

        this._inflight = inflight;
        return inflight;
    }

    async setLocation(loc_id) {
        this._invalidateCache();

        try {
            // Use the static import, not dynamic, to ensure same singleton instance
            const config = await storageManager.getItem('currentHangoutConfig') || {};

            const currentId = config.loc_id;
            const shouldTransition = currentId && currentId !== String(loc_id);

            // CRITICAL: Save the new location FIRST, before any transition effects
            // This ensures getCurrentBackground() returns the correct value during transitions
            config.loc_id = String(loc_id);
            delete config.backgroundUrl;
            await storageManager.setItem('currentHangoutConfig', config);

            // Now run transition effects (if applicable)
            if (shouldTransition) {
                try {
                    const { getLocationTransitionManager } = await import('../../js/hangout/ui/location-transition.js');
                    const transitionManager = getLocationTransitionManager();

                    const timeOfDay = await this._determineTimeOfDay(config);
                    await transitionManager.transitionToLocation(String(loc_id), timeOfDay);
                } catch { }
            }

            this.notifySubscribers('LOCATION_CHANGED', { locationId: String(loc_id) });
            return await this.getCurrentBackground();
        } catch (error) {
            throw error;
        }
    }

    async syncWithScenario() {
        try {
            // Check if there's already a valid user-set location (use static import)
            const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
            const currentLocId = currentConfig.loc_id;
            
            const hasValidUserLocation = currentLocId && 
                currentLocId !== '0' && 
                currentLocId !== '1' && 
                !String(currentLocId).startsWith('ERROR');
            
            if (hasValidUserLocation) {
                console.log('BackgroundService.syncWithScenario: Preserving user-set location:', currentLocId);
                return await this.getCurrentBackground();
            }
            
            const { HangoutState } = await import('../../js/hangout/state/HangoutState.js');
            const scenario = HangoutState.getInstance().getSelectedScenario();
            if (scenario?.loc) {
                await this.setLocation(scenario.loc);
            }
            return await this.getCurrentBackground();
        } catch {
            return await this.getCurrentBackground();
        }
    }

    async setTimeOfDay(timeOfDay) {
        this._invalidateCache();

        try {
            // Use static import for consistency
            const config = await storageManager.getItem('currentHangoutConfig') || {};

            if (!this.timeCodeMap[timeOfDay]) {
                throw new Error(`Invalid timeOfDay: ${timeOfDay}`);
            }

            config.timeOfDay = timeOfDay;
            delete config.backgroundUrl;
            await storageManager.setItem('currentHangoutConfig', config);

            this.notifySubscribers('TIME_CHANGED', { timeOfDay });
            return await this.getCurrentBackground();
        } catch (error) {
            throw error;
        }
    }

    async setBackground(loc_id, timeOfDay) {
        this._invalidateCache();

        try {
            // Use static import for consistency
            const config = await storageManager.getItem('currentHangoutConfig') || {};

            if (!this.timeCodeMap[timeOfDay]) {
                throw new Error(`Invalid timeOfDay: ${timeOfDay}`);
            }

            const currentId = config.loc_id;
            const locationChanged = currentId && currentId !== String(loc_id);
            const timeOnlyChange = !locationChanged && config.timeOfDay !== timeOfDay;

            // CRITICAL: Save the new location/time FIRST, before any transition effects
            // This ensures getCurrentBackground() returns the correct value during transitions
            config.loc_id = String(loc_id);
            config.timeOfDay = timeOfDay;
            delete config.backgroundUrl;
            await storageManager.setItem('currentHangoutConfig', config);

            // Now run transition effects (if applicable)
            if (locationChanged) {
                try {
                    const { getLocationTransitionManager } = await import('../../js/hangout/ui/location-transition.js');
                    const transitionManager = getLocationTransitionManager();
                    await transitionManager.transitionToLocation(String(loc_id), timeOfDay);
                } catch { }
            }

            const event = timeOnlyChange ? 'TIME_CHANGED' : 'BACKGROUND_CHANGED';
            this.notifySubscribers(event, { locationId: String(loc_id), timeOfDay });

            return await this.getCurrentBackground();
        } catch (error) {
            throw error;
        }
    }

    constructBackgroundUrl(loc_id, timeOfDay = 'night') {
        timeOfDay = this.numericTimeMap[timeOfDay] || timeOfDay;
        const timeCode = this.timeCodeMap[timeOfDay] || 3;
        return `${this.CDN_BASE_URL}${loc_id}.${timeCode}.webp`;
    }

    _normalizeTimeSlot(timeOfDay) {
        const mapped = this.numericTimeMap[timeOfDay] || timeOfDay;
        return this.timeCodeMap[mapped] ? mapped : 'night';
    }

    _imageLoads(url) {
        return new Promise((resolve) => {
            if (!url || typeof Image === 'undefined') {
                resolve(false);
                return;
            }
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
    }

    /**
     * Loads the requested day/sunset/night file, then the other two slots.
     * Throws only when every variation fails.
     * @param {string|number} locId
     * @param {string} [timeOfDay='night']
     * @returns {Promise<string>}
     */
    async resolveExistingBackgroundUrl(locId, timeOfDay = 'night') {
        const preferred = this._normalizeTimeSlot(timeOfDay);
        const slots = [preferred, ...['day', 'sunset', 'night'].filter((slot) => slot !== preferred)];
        for (const slot of slots) {
            const url = this.constructBackgroundUrl(locId, slot);
            if (await this._imageLoads(url)) return url;
        }
        throw new Error(`BackgroundService: no background image for location ${locId}`);
    }

    /**
     * @param {string} url
     * @returns {Promise<string>}
     */
    async resolveExistingBackgroundFromUrl(url) {
        const parsed = this.parseBackgroundUrl(url);
        if (parsed?.locationId) {
            return this.resolveExistingBackgroundUrl(parsed.locationId, parsed.timeOfDay);
        }
        if (await this._imageLoads(url)) return url;
        throw new Error(`BackgroundService: background failed to load: ${url}`);
    }

    parseBackgroundUrl(backgroundUrl) {
        if (!backgroundUrl) return null;
        try {
            const match = backgroundUrl.match(/(\d+)\.(\d+)\.webp/);
            return match ? { locationId: match[1], timeOfDay: this.timeSlotMap[match[2]] || 'night' } : null;
        } catch {
            return null;
        }
    }

    hourToTimeOfDay(hour) {
        if (hour >= 5 && hour < 17) return 'day';
        if (hour >= 17 && hour < 20) return 'sunset';
        return 'night';
    }

    async getLocationName(loc_id) {
        if (!loc_id) return '???';
        try {
            const locations = storage.get('background_loc_json');
            if (locations) {

                if (typeof locations === 'object') {
                    const locationName = locations[String(loc_id)];
                    if (locationName) return locationName;
                }
            }
            return `Location ${loc_id}`;
        } catch {
            return `Location ${loc_id}`;
        }
    }

    subscribe(event, callback) {
        const subscription = { event, callback };
        this.subscribers.push(subscription);
        return () => {
            const index = this.subscribers.indexOf(subscription);
            if (index > -1) this.subscribers.splice(index, 1);
        };
    }

    notifySubscribers(event, data) {
        this.subscribers
            .filter(sub => sub.event === event)
            .forEach(sub => {
                try { sub.callback(data); } catch { }
            });
    }

    async initialize() {
        try {
            // Use static import for consistency
            const config = await storageManager.getItem('currentHangoutConfig') || {};

            if (config.backgroundUrl && !config.loc_id) {
                const parsed = this.parseBackgroundUrl(config.backgroundUrl);
                if (parsed) {
                    config.loc_id = parsed.locationId;
                    // Legacy location property removed
                    if (!config.timeOfDay) config.timeOfDay = parsed.timeOfDay;
                }
            }

            if (!config.loc_id) {
                config.loc_id = '1';
            }
            if (!config.timeOfDay) config.timeOfDay = 'night';

            delete config.backgroundUrl;
            await storageManager.setItem('currentHangoutConfig', config);

            return await this.getCurrentBackground();
        } catch (error) {
            throw error;
        }
    }

    _invalidateCache() {
        this._cache = null;
        this._cacheTime = 0;
        this._inflight = null;
        this._cacheGeneration += 1;
    }

    invalidateCache() {
        this._invalidateCache();
    }

    // Static utility methods for stateless operations
    static getTimeSuffixFromTimeOfDay(timeOfDay) {
        const timeMap = {
            'day': '1',
            'sunset': '2',
            'night': '3'
        };
        return timeMap[timeOfDay] || 3;
    }

    static generateBackgroundUrl(locationId, timeOfDay = 'night') {
        if (!locationId) return null;
        const service = getBackgroundService();
        return service.constructBackgroundUrl(locationId, timeOfDay);
    }

    static parseBackgroundUrl(backgroundUrl) {
        if (!backgroundUrl || typeof backgroundUrl !== 'string') return null;
        const service = getBackgroundService();
        return service.parseBackgroundUrl(backgroundUrl);
    }

    static getBackgroundUrlFromState(stateDiff, fallbackState = {}) {
        // If backgroundUrl is explicitly stored, use it
        if (stateDiff.backgroundUrl) {
            return stateDiff.backgroundUrl;
        }

        // Generate from location and time
        const locationId = stateDiff.loc_id || stateDiff.loc || stateDiff.locationId || fallbackState.loc_id || fallbackState.locationId;
        const timeOfDay = stateDiff.timeOfDay || fallbackState.timeOfDay || 'day';

        return BackgroundService.generateBackgroundUrl(locationId, timeOfDay);
    }
}

let backgroundServiceInstance = null;
export function getBackgroundService() {
    if (typeof window !== 'undefined' && window.__backgroundService) {
        backgroundServiceInstance = window.__backgroundService;
        return backgroundServiceInstance;
    }
    if (!backgroundServiceInstance) backgroundServiceInstance = new BackgroundService();
    if (typeof window !== 'undefined') window.__backgroundService = backgroundServiceInstance;
    return backgroundServiceInstance;
}
